import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { debounce } from '@/utils';
import { DateRange } from '@/components/common/DateRangePicker';
import {
  // useGetCompetitionByIdQuery,
  // useGetEventsQuery,
  useGetPendingMarketsQuery,
  useGetMarketNamesQuery,
} from '@/store/api/eventApi';
import { MarketRunners, useLazyGetMarketRunnerQuery } from '@/store/api/runnersApi';
import {
  DeclareResultPayload,
  useDeclareResultMutation,
  useRollbackResultMutation,
} from '@/store/api/betResultApi';
import { PendingMarketItem } from '@/types/pendingMarket';
import { useGetRollbackMarketsQuery, useLazyGetFancyRunnersQuery } from '@/store/api/eventApi';
import dayjs from 'dayjs';

export type ResultValue = {
  status: 'Winner' | 'Looser' | 'ODD' | 'EVEN' | 'None';
  selectionId: string;
};

const sportOptions = [
  { label: 'All', value: 'all' },
  { label: 'Cricket', value: 'Cricket' },
  { label: 'Football', value: 'Soccer' },
  { label: 'Tennis', value: 'Tennis' },
  { label: 'Greyhound', value: 'Greyhound' },
  { label: 'Horse Racing', value: 'HorseRacing' },
];
export type ResultTabType = 'pending' | 'rollback';
type ResultActionType = 'declare' | 'rollback';

export const useEventListing = () => {
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [sport, setSport] = useState<string>('all');
  const [marketNameFilter, setMarketNameFilter] = useState<string>('all');
  const [activeResultTab, setActiveResultTab] = useState<ResultTabType>('pending');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: dayjs().startOf('day').toDate(),
    to: dayjs().endOf('day').toDate(),
  });

  // modal state
  const [open, setOpen] = useState(false);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [runners, setRunners] = useState<MarketRunners>({} as MarketRunners);
  const [eventDetails, setEventDetails] = useState<PendingMarketItem>();

  // APIs
  const { data, isLoading, isFetching } = useGetPendingMarketsQuery(
    {
      page,
      limit,
      search,
      ...(dateRange?.from && {
        fromDate: dayjs(dateRange.from).toISOString(),
      }),
      ...(dateRange?.to && {
        toDate: dayjs(dateRange.to).toISOString(),
      }),
      sport: sport !== 'all' ? sport : undefined,
      marketSearch: marketNameFilter !== 'all' ? marketNameFilter : undefined,
    },
    {
      skip: activeResultTab !== 'pending',
      pollingInterval: isPollingEnabled && activeResultTab === 'pending' ? 30000 : 0,
    }
  );

  const {
    data: rollbackData,
    isLoading: rollbackLoading,
    isFetching: rollbackFetching,
  } = useGetRollbackMarketsQuery(
    {
      page,
      limit,
      search,
      ...(dateRange?.from && {
        fromDate: dayjs(dateRange.from).toISOString(),
      }),
      ...(dateRange?.to && {
        toDate: dayjs(dateRange.to).toISOString(),
      }),
      sport: sport !== 'all' ? sport : undefined,
      marketSearch: marketNameFilter !== 'all' ? marketNameFilter : undefined,
    },
    {
      skip: activeResultTab !== 'rollback',
      pollingInterval: isPollingEnabled && activeResultTab === 'rollback' ? 30000 : 0,
    }
  );

  // MARKET NAMES (only relevant when a specific sport is selected)
  const { data: marketNamesData, isFetching: marketNamesLoading } = useGetMarketNamesQuery(
    {
      ...(dateRange?.from && { fromDate: dayjs(dateRange.from).toISOString() }),
      ...(dateRange?.to && { toDate: dayjs(dateRange.to).toISOString() }),
      sport,
      status: activeResultTab === 'pending' ? 'Pending' : 'Rollback',
    },
    { skip: sport === 'all' || activeResultTab !== 'pending' }
  );

  const marketNameOptions = [
    { label: 'All', value: 'all' },
    ...((marketNamesData?.data || []).map((name) => ({ label: name, value: name })) || []),
  ];

  const [fetchRunner, { isLoading: runnersLoading }] = useLazyGetMarketRunnerQuery();
  const [declareResult, { isLoading: betResultLoading }] = useDeclareResultMutation();
  const [rollbackResult, { isLoading: rollbackResultLoading }] = useRollbackResultMutation();
  const [fetchFancyRunners, { isLoading: fancyRunnersLoading }] = useLazyGetFancyRunnersQuery();
  const isActionLoading = betResultLoading || rollbackResultLoading;

  //events api
  // const { data: competitionData, isFetching: competitionLoading } = useGetCompetitionByIdQuery(sport, {
  //   skip: sport === 'all',
  // });
  // const competitionOptions = [
  //   ...(competitionData?.competitions?.map((c) => ({
  //     label: c.name,
  //     value: c.id,
  //   })) || []),
  // ];

  // const { data: eventsData, isFetching: eventsLoading } = useGetEventsQuery(
  //   { competitionId },
  //   {
  //     skip: !competitionId || sport === 'all',
  //   }
  // );
  // const eventOptions = [
  //   ...(eventsData?.events?.map((e) => ({
  //     label: e.name,
  //     value: e.id,
  //   })) || []),
  // ];

  const items = activeResultTab === 'pending' ? data?.markets || [] : rollbackData?.markets || [];

  const totalItems =
    activeResultTab === 'pending'
      ? data?.pagination?.totalItems || 0
      : rollbackData?.pagination?.totalItems || 0;

  // Debounced search
  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearch(value);
      setPage(1);
    }, 500)
  ).current;

  // Open Result Modal
  const handleOpenResult = async (row: PendingMarketItem) => {
    try {
      setSelectedEventName(row.eventName);
      setEventDetails(row);

      if (activeResultTab === 'rollback') {
        setRollbackConfirmOpen(true);
        return;
      }

      let runnersData = {} as MarketRunners;
      const isFancy = row.marketType?.toLowerCase() === 'fancy';
      const isKhado = row.marketName?.toLowerCase() === 'khado';
      const isMeter = row.marketName?.toLowerCase() === 'meter';
      const isCricketCasino = row.marketName?.toLowerCase() === 'cricketcasino';

      if (isFancy && isCricketCasino) {
        const response = await fetchFancyRunners({
          eventId: row.eventExternalId,
          marketId: row.marketId,
        }).unwrap();

        runnersData = {
          id: '',
          externalId: '',
          eventId: row.eventId,
          status: '',
          runner: response.runners.map((r) => ({
            runnerName: r.runnerName,
            selectionId: r.selectionId,
            handicap: 0,
            sortPriority: '',
            runnerId: r.selectionId,
          })),
        };
      } else if (row.marketType?.toLowerCase() === 'fancy') {
        runnersData = {
          id: '',
          externalId: '',
          eventId: '',
          status: '',
          runner: [
            {
              runnerName: row.selection || '',
              handicap: 0,
              selectionId: '',
              sortPriority: '',
              runnerId: '',
            },
          ],
        };
      } else {
        const response = await fetchRunner({
          eventExternalId: row.eventExternalId,
          marketId: row.marketId,
        }).unwrap();

        runnersData = response.marketRunners;
      }

      setRunners(runnersData);
      setOpen(true);
    } catch (error) {
      console.error(error);
    }
  };
  // Declare Result
  const handleResultAction = async (
    action: ResultActionType,
    data: Record<string, ResultValue>,
    runs: string,
    options?: { eventDetails?: PendingMarketItem; isCancel?: boolean }
  ): Promise<boolean> => {
    const details = options?.eventDetails ?? eventDetails;
    if (!details) return false;

    const { marketName, marketType, eventExternalId, marketId } = details;

    const isCancel = options?.isCancel;
    const basePayload: DeclareResultPayload = {
      market: marketName || '',
      marketType: marketType || '',
      eventId: eventExternalId || '',
      marketId: marketId || '',
    };

    const payload: DeclareResultPayload =
      action === 'rollback'
        ? basePayload
        : (() => {
            const winner = Object.values(data).find((r) => r.status === 'Winner');
            const looser = Object.values(data).find((r) => r.status === 'Looser');
            const isFancy = marketType?.toLowerCase() === 'fancy';
            const isRunsInputMarket = ['session', 'khado', 'meter'].includes(
              marketName?.toLowerCase() ?? ''
            );
            const isCricketCasino = marketName?.toLowerCase() === 'cricketcasino';
            const normalizedMarketName = marketName?.toLowerCase().replace(/[^a-z]/g, '');
            const isOddEven =
              normalizedMarketName === 'oddeven' || normalizedMarketName === 'oddseven';
            const oddEvenResult = Object.values(data).find(
              (result) => result.status === 'ODD' || result.status === 'EVEN'
            )?.status;
            let selectionId = '';

            if (isCancel) {
              selectionId = '-999';
            } else if (isOddEven) {
              selectionId = marketId || '';
            } else if (winner?.selectionId) {
              selectionId = winner.selectionId;
            } else if (isCricketCasino) {
              selectionId = looser?.selectionId || marketId || '';
            } else if (isFancy) {
              selectionId = marketId || '';
            }

            // Apply loss only for fancy markets outside session/khado/meter when no winner is selected.
            if (isFancy && !isRunsInputMarket && !isOddEven && !isCancel && !winner && selectionId) {
              selectionId = `${selectionId}_loss`;
            }

            const result = isCancel
              ? '-999'
              : isOddEven
                ? oddEvenResult || ''
                : runs || selectionId;
            return {
              ...basePayload,
              ...(isOddEven && { market: 'oddeven' }),
              selectionId,
              result,
            };
          })();
    // console.log(data,runs,payload);

    try {
      const res =
        action === 'declare'
          ? await declareResult(payload).unwrap()
          : await rollbackResult(payload).unwrap();

      toast.success(
        res?.message ||
          (action === 'declare'
            ? 'Result declared successfully!'
            : 'Result rolled back successfully!')
      );

      setOpen(false);
      setRollbackConfirmOpen(false);
      return true;
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Action failed');
      return false;
    }
  };
  const handleSportChange = (value: string) => {
    setSport(value);
    setMarketNameFilter('all');

    // if (value === 'all') {
    //   setCompetitionId(undefined);
    //   setEventId(undefined);
    // } else {
    //   setCompetitionId(undefined);
    //   setEventId(undefined);
    // }
  };

  const handleMarketNameChange = (value: string) => {
    setMarketNameFilter(value);
    setPage(1);
  };

  const handleResultTabChange = (tab: ResultTabType) => {
    setActiveResultTab(tab);
    setMarketNameFilter('all');
  };

  // const handleCompetitionChange = (value: number) => {
  //   setCompetitionId(value);
  //   setEventId(undefined);
  // };
  const isTableLoading =
    activeResultTab === 'pending'
      ? isLoading || (isFetching && !isPollingEnabled)
      : rollbackLoading || (rollbackFetching && !isPollingEnabled);

  return {
    // table data
    items,
    totalItems,
    isTableLoading,
    betResultLoading: isActionLoading,
    // runnersLoading,
    // loadingEventId

    // pagination & filters
    page,
    limit,
    setPage,
    setLimit,
    dateRange,
    setDateRange,
    debouncedSearch,
    isPollingEnabled,
    setIsPollingEnabled,

    // modal
    open,
    setOpen,
    rollbackConfirmOpen,
    setRollbackConfirmOpen,
    selectedEventName,
    lastResult: eventDetails?.result || '-',
    marketNameLabel: eventDetails?.marketName || '',
    runners,
    marketType: (eventDetails?.marketType || '').toLowerCase(),
    marketName: (eventDetails?.marketName || '').toLowerCase(),

    handleOpenResult,
    handleResultAction,

    sport,
    sportOptions,
    handleSportChange,
    marketNameFilter,
    marketNameOptions,
    marketNamesLoading,
    handleMarketNameChange,
    showMarketNameFilter: sport !== 'all' && activeResultTab === 'pending',
    activeResultTab,
    setActiveResultTab: handleResultTabChange,
  };
};
